import { HotelResultControls } from './HotelResultControls';
import { useBookingCaseContext } from '@frontend/features/travel-services/components/BookingCaseContext';
import { useHotelScrollAnchor } from './useHotelScrollAnchor';
import { useHotelContentBatch } from './hotelContentFlow';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HotelResultFilters } from './HotelResultFilters';
import { normalizedHotelFilters } from './searchModel';
import { HotelSearchForm } from './HotelSearchForm';
import { HotelResultCards, HotelResultEvidence, HotelPropertyDetail } from './HotelResultCards';
import { HotelSearchTransport } from './hotelSearchFlow';
import { useHotelSearchNavigation } from './useHotelSearchNavigation';
import type { HotelFare, HotelSearchDraft } from './searchModel';
import { HotelReservationDialog } from './HotelReservationDialog';
import { HotelResultsMap } from './HotelResultsMap';

export function HotelSearchPage({
  transport,
  onContextLockedChange,
  headerActions,
  initialDraft,
  restartNotice,
}: {
  transport: HotelSearchTransport;
  headerActions?: ReactNode;
  initialDraft?: HotelSearchDraft;
  restartNotice?: string;
  onContextLockedChange?: (locked: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { caseData } = useBookingCaseContext();
  const [editing, setEditing] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<string | null>(null);
  const [selectedFare, setSelectedFare] = useState<HotelFare | null>(null);
  const [view, setView] = useState<'list' | 'map'>('list');
  const {
    snapshot,
    error,
    committed,
    start,
    stop,
    changeFilters,
    changeOptions,
    catalogLimited,
    busy,
    reloadResults,
    cards,
    page,
    navigationKey,
    resultRange,
    hasPrevious,
    hasNext,
    previous,
    next,
    resultProgress,
  } = useHotelSearchNavigation(transport, view === 'map');
  useEffect(() => {
    onContextLockedChange?.(busy || selectedFare !== null);
  }, [busy, selectedFare, onContextLockedChange]);
  const resultsRef = useRef<HTMLDivElement>(null);
  useHotelScrollAnchor(resultsRef, cards, `${snapshot?.search?.search_id}:${navigationKey}`);
  useEffect(() => {
    resultsRef.current?.scrollIntoView({ block: 'start' });
  }, [navigationKey, snapshot?.search?.search_id]);
  const content = useHotelContentBatch(
    snapshot?.search?.search_id ?? 'none',
    (view === 'map' ? (snapshot?.cards ?? []) : cards).map((c) => c.property_id),
    i18n.language,
    transport,
  );
  return (
    <Box boxSizing="border-box" maxWidth={1200} width="100%" mx="auto" px={{ xs: 2, md: 3 }} py={3}>
      <Stack gap={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          gap={3}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4">{t('hotelSearch.title')}</Typography>
            <Typography>{t('hotelSearch.intro')}</Typography>
          </Box>
          {headerActions && <Box sx={{ flexShrink: 0, minWidth: 0 }}>{headerActions}</Box>}
        </Stack>
        {committed && !editing && (snapshot?.search || busy) && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
          >
            <Box>
              <Typography fontWeight={600}>{committed.location?.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(
                  new Date(`${committed.checkIn}T12:00:00`),
                )}
                {' – '}
                {new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(
                  new Date(`${committed.checkOut}T12:00:00`),
                )}
                {' · '}
                {t('hotelSearch.occupancySummary', {
                  rooms: committed.rooms.length,
                  guests: committed.rooms.reduce((count, room) => count + room.paxes.length, 0),
                })}
              </Typography>
            </Box>
            <Button onClick={() => setEditing(true)} disabled={busy} sx={{ flexShrink: 0 }}>
              {t('hotelSearch.modifySearch')}
            </Button>
          </Stack>
        )}
        {!committed && restartNotice && <Alert severity="info">{t(restartNotice)}</Alert>}
        {(!committed || editing || (!snapshot?.search && !busy)) && (
          <HotelSearchForm
            initial={committed ?? initialDraft}
            editing={!!snapshot?.search}
            onCancel={editing && snapshot?.search ? () => setEditing(false) : undefined}
            onSearch={(draft) => {
              setSelectedFare(null);
              setSelectedHotel(null);
              setEditing(false);
              start(draft);
            }}
            busy={busy}
          />
        )}
        {snapshot?.search && committed && !editing && (
          <Box>
            <HotelResultFilters
              value={normalizedHotelFilters(committed.filters)}
              disabled={['expired', 'stopped'].includes(snapshot.phase)}
              count={cards.length}
              summary={
                snapshot.totalCount != null
                  ? view === 'map'
                    ? t('hotelSearch.mapResultCount', { count: snapshot.totalCount })
                    : t('hotelSearch.visibleHotelCount', resultRange)
                  : undefined
              }
              onChange={(filters) => {
                setSelectedFare(null);
                setSelectedHotel(null);
                changeFilters(filters);
              }}
            />
            <HotelResultControls
              value={committed.results ?? { sort: 'selection' }}
              disabled={['expired', 'stopped'].includes(snapshot.phase)}
              onChange={(options) => {
                setSelectedFare(null);
                setSelectedHotel(null);
                changeOptions(options);
              }}
            />
            <ToggleButtonGroup
              exclusive
              value={view}
              aria-label={t('hotelSearch.resultsView')}
              onChange={(_, nextView: 'list' | 'map' | null) => {
                if (nextView) setView(nextView);
              }}
              sx={{ mt: 2 }}
            >
              <ToggleButton value="list" sx={{ minHeight: 44 }}>
                {t('hotelSearch.listView')}
              </ToggleButton>
              <ToggleButton value="map" sx={{ minHeight: 44 }}>
                {t('hotelSearch.mapView')}
              </ToggleButton>
            </ToggleButtonGroup>
            {catalogLimited && (
              <Typography variant="caption">
                {t('hotelSearch.catalogLimit', { count: snapshot.hotels.length })}
              </Typography>
            )}
          </Box>
        )}
        {busy && (
          <Stack direction="row" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography>
              {t(`hotelSearch.${snapshot?.phase}`)}{' '}
              {snapshot?.search?.executions?.length
                ? t('hotelSearch.executionProgress', {
                    done: snapshot.search.executions.filter((e) => ['completed', 'failed'].includes(e.status)).length,
                    total: snapshot.search.executions.length,
                  })
                : ''}
            </Typography>
            <Button onClick={stop}>{t('hotelSearch.stop')}</Button>
          </Stack>
        )}
        {error && (
          <Alert severity="error">
            {t(error === 'refreshStopped' ? 'hotelSearch.resultsRefreshStopped' : `hotelSearch.${error}`)}
            {(error === 'refreshStopped' || (view === 'map' && error === 'error' && snapshot?.search)) && (
              <Button onClick={reloadResults} disabled={busy}>
                {t('hotelSearch.reloadResults')}
              </Button>
            )}
          </Alert>
        )}
        {snapshot && ['failed', 'expired', 'stopped'].includes(snapshot.phase) && !error && (
          <Alert severity="warning">{t(`hotelSearch.${snapshot.phase}`)}</Alert>
        )}
        <HotelResultEvidence meta={snapshot?.resultMeta} />
        {snapshot?.phase === 'complete' && !snapshot.cards.length && (
          <Alert severity="info">
            {t(
              committed?.results?.name ||
                committed?.filters?.board_categories.length ||
                committed?.filters?.refundability
                ? 'hotelSearch.emptyFiltered'
                : snapshot.hotels.length
                  ? 'hotelSearch.empty'
                  : 'hotelSearch.noCatalog',
            )}
          </Alert>
        )}
        {content.error && (
          <Alert severity="warning">
            {t('hotelSearch.contentError')}
            <Button onClick={content.reload}>{t('hotelSearch.reloadContent')}</Button>
          </Alert>
        )}
        {snapshot?.search && (
          <Box ref={resultsRef}>
            {view === 'map' ? (
              <HotelResultsMap
                key={`${snapshot.search.search_id}:${JSON.stringify(committed?.filters)}:${JSON.stringify(committed?.results)}`}
                content={content.data}
                resolvedCount={content.resolvedCount}
                contentLoading={content.loading}
                cards={snapshot.cards}
                hotels={snapshot.hotels}
                options={committed?.results ?? { sort: 'selection' }}
                total={snapshot.totalCount}
                complete={!!snapshot.resultMeta && !snapshot.resultMeta.next_cursor}
                busy={busy}
                progress={resultProgress}
                onOpen={setSelectedHotel}
                onReload={reloadResults}
                onList={() => setView('list')}
              />
            ) : (
              <HotelResultCards
                content={content.data}
                contentLoading={content.loading}
                cards={cards}
                hotels={snapshot.hotels}
                onOpen={setSelectedHotel}
              />
            )}
          </Box>
        )}
        {view === 'list' && snapshot && (hasPrevious || hasNext || cards.length > 0) && (
          <Stack
            component="nav"
            aria-label={t('hotelSearch.resultPages')}
            direction="row"
            gap={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              disabled={!hasPrevious}
              onClick={() => {
                setSelectedFare(null);
                setSelectedHotel(null);
                previous();
              }}
            >
              {t('hotelSearch.previousResults')}
            </Button>
            <Typography role="status">{t('hotelSearch.resultPage', { page })}</Typography>
            <Button
              disabled={!hasNext || busy}
              onClick={() => {
                setSelectedFare(null);
                setSelectedHotel(null);
                next();
              }}
            >
              {t('hotelSearch.nextResults')}
            </Button>
          </Stack>
        )}
      </Stack>
      {selectedHotel && snapshot?.search && committed && (
        <HotelPropertyDetail
          key={`${snapshot.search.search_id}:${selectedHotel}:${JSON.stringify(committed.filters)}`}
          searchId={snapshot.search.search_id}
          hotel={snapshot.hotels.find((h) => h.hotel_id === selectedHotel)!}
          filters={normalizedHotelFilters(committed.filters)}
          transport={transport}
          onClose={() => setSelectedHotel(null)}
          onReserve={setSelectedFare}
          polling={snapshot.search.status === 'accepted' || snapshot.search.status === 'running'}
        />
      )}
      {selectedFare && (
        <HotelReservationDialog
          key={selectedFare.fare_id}
          fare={selectedFare}
          initialCase={caseData}
          checkIn={committed?.checkIn}
          onBookingSubmitted={(id, agencyId) =>
            navigate(`/dashboard/travel-bookings?hotelReservationId=${encodeURIComponent(id)}&agencyId=${agencyId}`)
          }
          onClose={() => setSelectedFare(null)}
        />
      )}
    </Box>
  );
}
